import React, { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import axios from 'axios';
import { RefreshCw, Play, Users, FileText, TrendingUp, AlertCircle } from 'lucide-react';
import toast from 'react-hot-toast';

interface DashboardStats {
  stats: {
    totalArticles: number;
    publishedArticles: number;
    draftArticles: number;
    totalUsers: number;
    totalSubscribers: number;
    totalInteractions: number;
  };
  recentArticles: any[];
  topUsers: any[];
}

const AdminPage: React.FC = () => {
  const [authToken, setAuthToken] = useState(() => 
    localStorage.getItem('adminToken') || ''
  );
  const [isProcessing, setIsProcessing] = useState(false);

  // Dashboard data query
  const { data: dashboardData, isLoading, error, refetch } = useQuery<DashboardStats>({
    queryKey: ['admin-dashboard'],
    queryFn: async () => {
      const response = await axios.get('/api/v1/admin/dashboard', {
        headers: { Authorization: `Bearer ${authToken}` }
      });
      return response.data.data;
    },
    enabled: !!authToken
  });

  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    const form = e.target as HTMLFormElement;
    const email = (form.elements.namedItem('email') as HTMLInputElement).value;
    const password = (form.elements.namedItem('password') as HTMLInputElement).value;
    try {
      const res = await axios.post('/api/v1/admin/login', { email, password });
      if (res.data.success) {
        setAuthToken(res.data.token);
        localStorage.setItem('adminToken', res.data.token);
        toast.success('Logged in to admin panel');
      }
    } catch {
      toast.error('Invalid credentials');
    }
  };

  const handleProcessNews = async () => {
    if (isProcessing) return;
    
    setIsProcessing(true);
    try {
      await axios.post('/api/v1/admin/process-news', {}, {
        headers: { Authorization: `Bearer ${authToken}` }
      });
      
      toast.success('News processing started in background');
      setTimeout(() => refetch(), 2000);
      
    } catch (error) {
      toast.error('Failed to start news processing');
    } finally {
      setIsProcessing(false);
    }
  };

  const handlePublishDrafts = async () => {
    try {
      await axios.post('/api/v1/admin/publish-drafts', {}, {
        headers: { Authorization: `Bearer ${authToken}` }
      });
      
      toast.success('Draft articles published successfully');
      refetch();
      
    } catch (error) {
      toast.error('Failed to publish drafts');
    }
  };

  // Login form
  if (!authToken) {
    return (
      <div className="min-h-screen bg-primary-50 flex items-center justify-center">
        <div className="bg-white newspaper-border p-8 max-w-md w-full mx-4">
          <h1 className="font-playfair text-2xl font-bold text-primary-900 mb-6 text-center">
            Admin Access
          </h1>
          <form onSubmit={handleLogin}>
            <div className="mb-4">
              <label className="block text-primary-700 text-sm font-medium mb-2">
                Email
              </label>
              <input
                name="email"
                type="email"
                placeholder="admin@example.com"
                className="form-input"
              />
            </div>
            <div className="mb-4">
              <label className="block text-primary-700 text-sm font-medium mb-2">
                Password
              </label>
              <input
                name="password"
                type="password"
                placeholder="Enter password"
                className="form-input"
              />
            </div>
            <button type="submit" className="btn-primary w-full">
              Login
            </button>
          </form>
        </div>
      </div>
    );
  }

  // Error state
  if (error) {
    return (
      <div className="container-news py-12">
        <div className="text-center">
          <AlertCircle className="h-16 w-16 text-red-500 mx-auto mb-4" />
          <h2 className="font-playfair text-2xl font-bold text-primary-900 mb-4">
            Access Denied
          </h2>
          <p className="text-primary-600 mb-6">
            Invalid credentials or server error.
          </p>
          <button 
            onClick={() => {
              setAuthToken('');
              localStorage.removeItem('adminToken');
            }}
            className="btn-secondary"
          >
            Logout & Retry
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-primary-50 min-h-screen">
      {/* Header */}
      <div className="bg-white border-b border-primary-200">
        <div className="container-news py-6">
          <div className="flex justify-between items-center">
            <div>
              <h1 className="font-playfair text-3xl font-bold text-primary-900">
                Admin Dashboard
              </h1>
              <p className="text-primary-600 mt-1">
                Manage Unbiased News operations
              </p>
            </div>
            <div className="flex space-x-3">
              <button
                onClick={handleProcessNews}
                disabled={isProcessing}
                className="btn-accent flex items-center disabled:opacity-50"
              >
                {isProcessing ? (
                  <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
                ) : (
                  <Play className="h-4 w-4 mr-2" />
                )}
                {isProcessing ? 'Processing...' : 'Process News'}
              </button>
              <button
                onClick={handlePublishDrafts}
                className="btn-secondary flex items-center"
              >
                <FileText className="h-4 w-4 mr-2" />
                Publish Drafts
              </button>
              <button
                onClick={() => refetch()}
                className="btn-secondary flex items-center"
              >
                <RefreshCw className="h-4 w-4 mr-2" />
                Refresh
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Dashboard Content */}
      <div className="container-news py-8">
        {isLoading ? (
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {[...Array(6)].map((_, i) => (
              <div key={i} className="bg-white newspaper-border p-6">
                <div className="loading-skeleton h-4 mb-2"></div>
                <div className="loading-skeleton h-8"></div>
              </div>
            ))}
          </div>
        ) : dashboardData ? (
          <>
            {/* Stats Grid */}
            <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-6 gap-6 mb-8">
              <div className="bg-white newspaper-border p-6 text-center">
                <FileText className="h-8 w-8 text-accent-600 mx-auto mb-2" />
                <div className="text-2xl font-bold text-primary-900">
                  {dashboardData.stats.totalArticles}
                </div>
                <div className="text-sm text-primary-600">Total Articles</div>
              </div>
              
              <div className="bg-white newspaper-border p-6 text-center">
                <TrendingUp className="h-8 w-8 text-green-600 mx-auto mb-2" />
                <div className="text-2xl font-bold text-primary-900">
                  {dashboardData.stats.publishedArticles}
                </div>
                <div className="text-sm text-primary-600">Published</div>
              </div>
              
              <div className="bg-white newspaper-border p-6 text-center">
                <FileText className="h-8 w-8 text-yellow-600 mx-auto mb-2" />
                <div className="text-2xl font-bold text-primary-900">
                  {dashboardData.stats.draftArticles}
                </div>
                <div className="text-sm text-primary-600">Drafts</div>
              </div>
              
              <div className="bg-white newspaper-border p-6 text-center">
                <Users className="h-8 w-8 text-blue-600 mx-auto mb-2" />
                <div className="text-2xl font-bold text-primary-900">
                  {dashboardData.stats.totalUsers}
                </div>
                <div className="text-sm text-primary-600">Registered Users</div>
              </div>
              
              <div className="bg-white newspaper-border p-6 text-center">
                <Users className="h-8 w-8 text-purple-600 mx-auto mb-2" />
                <div className="text-2xl font-bold text-primary-900">
                  {dashboardData.stats.totalSubscribers}
                </div>
                <div className="text-sm text-primary-600">Subscribers</div>
              </div>
              
              <div className="bg-white newspaper-border p-6 text-center">
                <TrendingUp className="h-8 w-8 text-accent-600 mx-auto mb-2" />
                <div className="text-2xl font-bold text-primary-900">
                  {dashboardData.stats.totalInteractions}
                </div>
                <div className="text-sm text-primary-600">Interactions</div>
              </div>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
              {/* Recent Articles */}
              <div className="bg-white newspaper-border p-6">
                <h3 className="font-playfair text-xl font-bold text-primary-900 mb-4">
                  Recent Articles
                </h3>
                <div className="space-y-4">
                  {dashboardData.recentArticles.slice(0, 5).map((article) => (
                    <div key={article.id} className="border-b border-primary-100 pb-3">
                      <div className="flex justify-between items-start">
                        <div className="flex-1">
                          <h4 className="font-medium text-primary-900 text-sm mb-1">
                            {article.title?.substring(0, 60)}...
                          </h4>
                          <div className="flex items-center space-x-2 text-xs text-primary-500">
                            <span className="category-badge">
                              {article.category}
                            </span>
                            <span className={`px-2 py-1 rounded text-xs ${
                              article.status === 'published' 
                                ? 'bg-green-100 text-green-800' 
                                : 'bg-yellow-100 text-yellow-800'
                            }`}>
                              {article.status}
                            </span>
                          </div>
                        </div>
                        <div className="text-xs text-primary-500">
                          Impact: {parseFloat(article.noticiabilityTotal || 0).toFixed(0)}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Top Users */}
              <div className="bg-white newspaper-border p-6">
                <h3 className="font-playfair text-xl font-bold text-primary-900 mb-4">
                  Top Users by Impact
                </h3>
                <div className="space-y-4">
                  {dashboardData.topUsers.slice(0, 5).map((user, index) => (
                    <div key={user.email} className="flex items-center justify-between">
                      <div className="flex items-center space-x-3">
                        <div className="w-8 h-8 bg-accent-100 text-accent-600 rounded-full flex items-center justify-center text-sm font-bold">
                          #{index + 1}
                        </div>
                        <div>
                          <div className="font-medium text-primary-900 text-sm">
                            {user.email.substring(0, 20)}...
                          </div>
                          <div className="text-xs text-primary-500">
                            {user.totalArticlesRead} articles read
                          </div>
                        </div>
                      </div>
                      <div className="text-right">
                        <div className="font-bold text-accent-600">
                          {parseFloat(user.totalEthixStaked || 0).toFixed(1)} ETHIX
                        </div>
                        <div className="text-xs text-primary-500">staked</div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </>
        ) : null}
      </div>

      {/* Status Bar */}
      <div className="fixed bottom-0 right-0 m-6 bg-white newspaper-border p-4 max-w-sm">
        <div className="flex items-center space-x-2">
          <div className="w-2 h-2 bg-green-500 rounded-full animate-pulse"></div>
          <span className="text-sm text-primary-700">System Operational</span>
        </div>
        <div className="text-xs text-primary-500 mt-1">
          Last update: {new Date().toLocaleTimeString()}
        </div>
      </div>
    </div>
  );
};

export default AdminPage;